import React, { createContext, useContext, useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";

type Theme = "light" | "dark";
const Context = createContext<{ dark: boolean; setTheme: (theme: Theme) => void }>({
	dark: false,
	setTheme: () => {},
});
export function ThemeProvider({ children }: { children: React.ReactNode }) {
	const [theme, setTheme] = useState<Theme | null>(() => {
		try {
			const stored = localStorage.getItem("pos-theme");
			return stored === "light" || stored === "dark" ? stored : null;
		} catch {
			return null;
		}
	});
	const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
	const dark = theme === null ? systemDark : theme === "dark";

	useEffect(() => {
		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const sync = () => setSystemDark(media.matches);
		sync();
		media.addEventListener("change", sync);
		return () => media.removeEventListener("change", sync);
	}, []);
	useEffect(() => {
		document.documentElement.classList.toggle("dark", dark);
		document.documentElement.classList.toggle("light", !dark);
	}, [dark]);
	useEffect(() => {
		try {
			if (theme === null) localStorage.removeItem("pos-theme");
			else localStorage.setItem("pos-theme", theme);
		} catch {
			/* Storage may be disabled. */
		}
	}, [theme]);
	return <Context.Provider value={{ dark, setTheme }}>{children}</Context.Provider>;
}
export function ThemeToggle() {
	const { dark, setTheme } = useContext(Context);
	const { i18n } = useTranslation();
	const ar = i18n.language.startsWith("ar");
	const title = ar
		? dark
			? "التبديل إلى الوضع الفاتح"
			: "التبديل إلى الوضع الداكن"
		: dark
			? "Switch to light mode"
			: "Switch to dark mode";
	return (
		<Button
			type="button"
			variant="outline"
			role="switch"
			aria-checked={dark}
			aria-label={ar ? "الوضع الداكن" : "Dark mode"}
			title={title}
			onClick={() => setTheme(dark ? "light" : "dark")}
			className="relative grid h-11 w-20 shrink-0 grid-cols-2 items-center rounded-full p-1"
		>
			<span
				aria-hidden="true"
				className={`absolute inset-y-1 w-8 rounded-full bg-primary transition-all motion-reduce:transition-none ${dark ? "end-1" : "start-1"}`}
			/>
			<Sun
				aria-hidden="true"
				className={`relative z-10 mx-auto h-4 w-4 ${dark ? "text-muted-foreground" : "text-primary-foreground"}`}
			/>
			<Moon
				aria-hidden="true"
				className={`relative z-10 mx-auto h-4 w-4 ${dark ? "text-primary-foreground" : "text-muted-foreground"}`}
			/>
		</Button>
	);
}
